import { useEffect, useRef, useState } from "react";
import { gameHighlights } from "../../../mocks/gameData";

export default function GameHighlights() {
  const [visible, setVisible] = useState(false);
  const [activeFilter, setActiveFilter] = useState("All");
  const ref = useRef<HTMLElement>(null);

  const filters = ["All", "FPS", "RPG", "MOBA", "Ranked"];
  const filterMap: Record<string, string[]> = {
    All: [],
    FPS: ["Call of Duty: Warzone", "Valorant"],
    RPG: ["Elden Ring", "World of Warcraft"],
    MOBA: ["League of Legends"],
    Ranked: ["Apex Legends", "Valorant"],
  };

  const displayed =
    activeFilter === "All"
      ? gameHighlights
      : gameHighlights.filter((g) => filterMap[activeFilter]?.includes(g.game));

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="highlights" ref={ref} className="relative py-28 bg-[#0a0814] overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#22d3ee] to-transparent opacity-30" />
      <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#e879f9] to-transparent opacity-20" />

      <div className="relative max-w-7xl mx-auto px-6">
        {/* Header */}
        <div
          className={`flex flex-col items-center text-center mb-12 transition-all duration-700 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <span className="font-mono-tech text-xs text-[#22d3ee] uppercase tracking-widest mb-3">// best plays</span>
          <h2 className="font-orbitron text-3xl md:text-5xl font-black uppercase text-white mb-2">
            Game <span className="neon-text-cyan">Highlights</span>
          </h2>
          <p className="font-rajdhani text-base text-slate-400 max-w-xl">
            Clutch moments, insane plays, and record-breaking runs — all captured in these highlights.
          </p>
        </div>

        {/* Filters */}
        <div className="flex items-center justify-center gap-3 mb-10 flex-wrap">
          {filters.map((f) => (
            <button
              key={f}
              onClick={() => setActiveFilter(f)}
              className={`font-orbitron text-xs uppercase tracking-widest px-4 py-1.5 rounded-full border transition-all duration-300 cursor-pointer whitespace-nowrap ${
                activeFilter === f
                  ? "border-[#e879f9] text-[#e879f9] bg-[#e879f9]/10"
                  : "border-white/10 text-slate-400 hover:border-[#e879f9]/40 hover:text-[#e879f9]/70"
              }`}
            >
              {f}
            </button>
          ))}
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {displayed.map((clip, i) => (
            <div
              key={clip.id}
              className={`card-gaming rounded-xl overflow-hidden cursor-pointer group transition-all duration-700 ${
                visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"
              }`}
              style={{ transitionDelay: `${i * 80}ms` }}
            >
              {/* Thumbnail */}
              <div className="relative w-full h-44 overflow-hidden">
                <img
                  src={clip.image}
                  alt={clip.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0e0c1e] via-transparent to-transparent" />
                {/* Tag */}
                <div className="absolute top-3 left-3 px-2 py-0.5 rounded font-mono-tech text-xs text-[#07070f] bg-[#e879f9] font-bold">
                  {clip.tag}
                </div>
                {/* Play overlay */}
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#e879f9]/20 border border-[#e879f9]/60 backdrop-blur-sm">
                    <i className="ri-play-fill text-[#e879f9] text-xl" />
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-5">
                <p className="font-mono-tech text-xs text-[#22d3ee] mb-1 uppercase tracking-wider">{clip.game}</p>
                <h3 className="font-orbitron text-sm font-bold text-white mb-2">{clip.title}</h3>
                <p className="font-rajdhani text-sm text-slate-400 leading-relaxed mb-4">{clip.description}</p>

                {/* Stats row */}
                <div className="flex items-center gap-4 pt-3 border-t border-white/5">
                  <div className="flex items-center gap-1.5">
                    <i className="ri-sword-line text-[#e879f9] text-sm" />
                    <span className="font-mono-tech text-xs text-slate-300">{clip.kills} Kills</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <i className="ri-fire-line text-[#22d3ee] text-sm" />
                    <span className="font-mono-tech text-xs text-slate-300">{clip.damage} Dmg</span>
                  </div>
                  <div className="flex items-center gap-1.5 ml-auto">
                    <i className="ri-trophy-line text-yellow-400 text-sm" />
                    <span className="font-mono-tech text-xs text-yellow-400">{clip.placement}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="flex justify-center mt-12">
          <a
            href="#streams"
            onClick={(e) => {
              e.preventDefault();
              document.querySelector("#streams")?.scrollIntoView({ behavior: "smooth" });
            }}
            className="font-orbitron text-xs uppercase tracking-widest px-8 py-3 rounded-md border border-[#22d3ee]/40 text-[#22d3ee] hover:bg-[#22d3ee]/10 transition-all duration-300 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-youtube-line mr-2" />
            View All Clips on YouTube
          </a>
        </div>
      </div>
    </section>
  );
}
